/**
 * <sticky-add-to-cart> — fixed ATC bar shown after the product section
 * leaves the viewport. Submits the matching product form via requestSubmit.
 * On mobile (bottom bar), hidden again when the site footer enters the viewport.
 * On desktop (top bar), footer hide is skipped; --sticky-atc-height is set
 * on documentElement while visible so sticky page content can clear the bar.
 * Loading state mirrors the main [data-sticky-atc-sentinel] aria-busy flag.
 * Price amount mirrors the lowest available PDP supply option.
 *
 * Contract:
 *   data-product-id              — product id shared with the main buy form
 *   data-scroll-target           — optional element id; when set, CTA scrolls instead of ATC
 *   [data-sticky-atc-section]    — product section observed for visibility
 *   [data-sticky-atc-sentinel]   — main buy button for loader / disabled sync
 *   [data-sticky-atc-footer]     — footer observed to hide the sticky bar (mobile only)
 *   form[data-sticky-atc-form]   — form to submit on sticky button click
 *   [data-sticky-add-to-cart]    — sticky CTA button inside this element
 *   [data-sticky-price]          — lowest available supply option price
 */
import { ThemeEvents } from '@theme/events';
import { formatThemeMoney } from '@theme/utilities';

class StickyAddToCart extends HTMLElement {
  static #visibleClass = 'sticky-add-to-cart--visible';
  static #desktopQuery = '(min-width: 990px)';
  static #heightVar = '--sticky-atc-height';

  #controller = null;
  #sectionObserver = null;
  #footerObserver = null;
  #loadingObserver = null;
  #desktopMedia = null;
  #button = null;
  #mainButton = null;
  #priceAmount = null;
  #sectionVisible = true;
  #footerVisible = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#disconnectObservers();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const productId = this.dataset.productId;

    if (!productId) return;

    this.#button = this.querySelector('[data-sticky-add-to-cart]');
    this.#priceAmount = this.querySelector('[data-sticky-price]');
    this.#mainButton = document.querySelector(
      `[data-sticky-atc-sentinel][data-product-id="${productId}"]`,
    );

    const productSection = document.querySelector('[data-sticky-atc-section]');

    if (!(productSection instanceof Element)) return;

    this.#desktopMedia = window.matchMedia(StickyAddToCart.#desktopQuery);
    this.#desktopMedia.addEventListener('change', this.#handleBreakpointChange.bind(this), { signal });

    this.#sectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry) return;

        this.#sectionVisible = entry.isIntersecting;
        this.#syncVisibility();
      },
      { threshold: 0, rootMargin: '0px' },
    );

    this.#sectionObserver.observe(productSection);

    this.#setupFooterObserver();

    if (this.#mainButton instanceof Element && !this.dataset.scrollTarget) {
      this.#loadingObserver = new MutationObserver(() => {
        this.#syncLoadingFromMain();
      });

      this.#loadingObserver.observe(this.#mainButton, {
        attributes: true,
        attributeFilter: ['aria-busy', 'disabled', 'aria-disabled'],
      });

      this.#syncLoadingFromMain();
    }

    this.#button?.addEventListener('click', this.#handleClick.bind(this), { signal });

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate.bind(this), { signal });
    document.addEventListener(ThemeEvents.purchaseTypeUpdate, this.#onPurchaseTypeUpdate.bind(this), { signal });

    this.#schedulePriceSync();
  }

  disconnectedCallback() {
    this.#disconnectObservers();
    this.#controller?.abort();
    this.#controller = null;
    this.#desktopMedia = null;
    this.#button = null;
    this.#mainButton = null;
    this.#priceAmount = null;
    document.documentElement.style.removeProperty(StickyAddToCart.#heightVar);
  }

  #disconnectObservers() {
    this.#sectionObserver?.disconnect();
    this.#sectionObserver = null;
    this.#footerObserver?.disconnect();
    this.#footerObserver = null;
    this.#loadingObserver?.disconnect();
    this.#loadingObserver = null;
  }

  #isDesktop() {
    return this.#desktopMedia?.matches ?? window.matchMedia(StickyAddToCart.#desktopQuery).matches;
  }

  #setupFooterObserver() {
    this.#footerObserver?.disconnect();
    this.#footerObserver = null;

    if (this.#isDesktop()) {
      this.#footerVisible = false;
      return;
    }

    const footer = document.querySelector('[data-sticky-atc-footer]');

    if (!(footer instanceof Element)) {
      if (document.readyState === 'loading') {
        document.addEventListener(
          'DOMContentLoaded',
          () => {
            this.#setupFooterObserver();
          },
          { once: true, signal: this.#controller?.signal },
        );
      }

      return;
    }

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry) return;

        this.#footerVisible = entry.isIntersecting;
        this.#syncVisibility();
      },
      { threshold: 0, rootMargin: '0px' },
    );

    this.#footerObserver.observe(footer);
  }

  #handleBreakpointChange() {
    this.#setupFooterObserver();
    this.#syncVisibility();
  }

  #syncVisibility() {
    const hideForFooter = !this.#isDesktop() && this.#footerVisible;
    this.#setVisible(!this.#sectionVisible && !hideForFooter);
  }

  #setVisible(visible) {
    this.classList.toggle(StickyAddToCart.#visibleClass, visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    this.#syncLayoutHeight(visible);
  }

  #syncLayoutHeight(visible) {
    if (this.#isDesktop() && visible) {
      document.documentElement.style.setProperty(
        StickyAddToCart.#heightVar,
        `${this.offsetHeight}px`,
      );
      return;
    }

    document.documentElement.style.setProperty(StickyAddToCart.#heightVar, '0px');
  }

  #onVariantUpdate(event) {
    if (!this.#isEventForProduct(event)) return;
    this.#schedulePriceSync();
  }

  #onPurchaseTypeUpdate(event) {
    if (!this.#isEventForProduct(event)) return;
    this.#schedulePriceSync();
  }

  #isEventForProduct(event) {
    const productId = this.dataset.productId;
    const detail = event?.detail;
    const eventProductId = detail?.productId || detail?.data?.productId;

    if (!productId || !eventProductId) return true;

    return String(eventProductId) === String(productId);
  }

  #schedulePriceSync() {
    requestAnimationFrame(() => {
      this.#syncPriceFromPdp();
    });
  }

  #syncPriceFromPdp() {
    const priceAmount = this.#priceAmount;
    const productId = this.dataset.productId;

    if (!(priceAmount instanceof HTMLElement) || !productId) return;

    const selector = document.querySelector(
      `product-supply-selector[data-product-id="${productId}"]`,
    );

    if (!(selector instanceof Element)) return;

    if (typeof selector.getLowestAvailableOptionPrice !== 'function') return;

    const lowestPrice = selector.getLowestAvailableOptionPrice();
    if (!Number.isFinite(lowestPrice)) return;

    priceAmount.textContent = formatThemeMoney(lowestPrice, {
      moneyFormat: selector.dataset.moneyFormat || '{{amount}}',
      moneyWithCurrencyFormat: selector.dataset.moneyWithCurrencyFormat || '{{amount}}',
      currency: selector.dataset.currency || 'USD',
    });
  }

  #syncLoadingFromMain() {
    const button = this.#button;
    const main = this.#mainButton;

    if (!(button instanceof HTMLButtonElement) || !(main instanceof HTMLButtonElement)) {
      return;
    }

    const loading = main.getAttribute('aria-busy') === 'true';
    const available = !main.disabled && main.getAttribute('aria-disabled') !== 'true';
    const textElement = button.querySelector('.add-to-cart__label');
    const defaultLabel = button.dataset.labelDefault || '';
    const loadingLabel = button.dataset.labelLoading || defaultLabel;

    if (loading) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-label', loadingLabel);

      if (textElement instanceof HTMLElement) {
        textElement.textContent = loadingLabel;
      }

      return;
    }

    button.removeAttribute('aria-busy');
    button.disabled = !available;
    button.setAttribute('aria-disabled', available ? 'false' : 'true');
    button.setAttribute('aria-label', defaultLabel);

    if (textElement instanceof HTMLElement) {
      textElement.textContent = defaultLabel;
    }
  }

  #handleClick() {
    const button = this.#button;

    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const scrollTarget = this.dataset.scrollTarget?.trim();

    if (scrollTarget) {
      this.#scrollToTarget(scrollTarget);
      return;
    }

    const productId = this.dataset.productId;

    if (!productId) return;

    const form = document.querySelector(
      `form[data-sticky-atc-form][data-product-id="${productId}"]`,
    );

    if (!(form instanceof HTMLFormElement)) return;

    form.requestSubmit();
  }

  #scrollToTarget(targetId) {
    const id = targetId.replace(/^#/, '');

    if (!id) return;

    const target = document.getElementById(id);

    if (!(target instanceof Element)) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
